// Contribution logging form component
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { supabase } from '@/lib/supabase'
import { useUser } from '@/lib/hooks'
import { useProject } from '@/contexts/project-context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, Calendar } from 'lucide-react'
import { format } from 'date-fns'

const contributionSchema = z.object({
  task_id: z.string().min(1, 'Please select a task'),
  description: z.string().min(10, 'Please provide a meaningful description'),
  contribution_date: z.string(),
  effort_value: z.number().min(0).optional(),
  capital_value: z.number().min(0).optional(),
  qualitative_value: z.string().optional(),
})

type ContributionFormData = z.infer<typeof contributionSchema>

interface Task {
  id: string
  title: string
  category: string | null
}

interface ContributionFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export default function ContributionForm({ open, onOpenChange, onSuccess }: ContributionFormProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [tasks, setTasks] = useState<Task[]>([])
  const { profile: user } = useUser()
  const { currentProject } = useProject()

  const form = useForm<ContributionFormData>({
    resolver: zodResolver(contributionSchema),
    defaultValues: {
      task_id: '',
      description: '',
      contribution_date: format(new Date(), 'yyyy-MM-dd'),
      effort_value: 0,
      capital_value: 0,
      qualitative_value: '',
    },
  })

  useEffect(() => {
    // Fetch available tasks
    const fetchTasks = async () => {
      if (!currentProject) return
      
      const { data, error } = await supabase
        .from('tasks')
        .select('id, title, category')
        .eq('project_id', currentProject.id)
        .order('category', { ascending: true })
        .order('title', { ascending: true })

      if (!error && data) {
        setTasks(data)
      }
    }

    if (open && currentProject) {
      fetchTasks()
    }
  }, [open, currentProject])

  const onSubmit = async (data: ContributionFormData) => {
    if (!user) return

    setLoading(true)
    setError(null)

    try {
      if (!currentProject) {
        throw new Error('No project selected')
      }
      
      const contributionData = {
        ...data,
        user_id: user.id,
        project_id: currentProject.id,
        contribution_date: new Date(data.contribution_date).toISOString(),
        effort_value: data.effort_value || null,
        capital_value: data.capital_value || null,
        qualitative_value: data.qualitative_value || null,
      }

      const { error } = await supabase
        .from('contributions')
        .insert(contributionData)

      if (error) throw error

      onSuccess?.()
      onOpenChange(false)
      form.reset()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const groupedTasks = tasks.reduce((acc, task) => {
    const category = task.category || 'Uncategorized'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(task)
    return acc
  }, {} as Record<string, Task[]>)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Log Contribution</DialogTitle>
          <DialogDescription>
            Record your work and contributions to the company. Be specific about what you accomplished.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="task">Task</Label>
            <Select
              value={form.watch('task_id')?.toString()}
              onValueChange={(value) => form.setValue('task_id', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a task" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(groupedTasks).map(([category, categoryTasks]) => (
                  <div key={category}>
                    <div className="px-2 py-1.5 text-sm font-semibold text-gray-500">
                      {category}
                    </div>
                    {categoryTasks.map((task) => (
                      <SelectItem key={task.id} value={task.id.toString()}>
                        {task.title}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.task_id && (
              <p className="text-sm text-red-600">{form.formState.errors.task_id.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="contribution_date">Date</Label>
            <div className="relative">
              <Input
                id="contribution_date"
                type="date"
                {...form.register('contribution_date')}
                className="pl-10"
              />
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...form.register('description')}
              placeholder="Describe what you accomplished, deliverables completed, or progress made..."
              rows={4}
            />
            {form.formState.errors.description && (
              <p className="text-sm text-red-600">{form.formState.errors.description.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="effort_value">Effort Hours (Optional)</Label>
              <Input
                id="effort_value"
                type="number"
                step="0.5"
                min="0"
                {...form.register('effort_value', { valueAsNumber: true })}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="capital_value">Capital Invested (Optional)</Label>
              <Input
                id="capital_value"
                type="number"
                step="0.01"
                min="0"
                {...form.register('capital_value', { valueAsNumber: true })}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="qualitative_value">Additional Context (Optional)</Label>
            <Textarea
              id="qualitative_value"
              {...form.register('qualitative_value')}
              placeholder="Any additional context, strategic insights, or qualitative impact..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Log Contribution
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}